import { useCallback, useMemo, useState } from 'react';
import { ApiError } from '../../../lib/http/ApiError';
import { useFetch } from '../../../hooks/useFetch';
import { useNotification } from '../../notifications/hooks/useNotification';
import { createUsersApi } from '../api/usersApi';
import type { User } from '../userTypes';

interface UseAssignUserProfileResult {
  assignUserProfile: (id: string, profileId: string) => Promise<User | null>;
  loading: boolean;
  error: ApiError | Error | null;
}

/** Mutation hook for PUT /users/:id/profile (assign/replace the rights profile). */
export function useAssignUserProfile(): UseAssignUserProfileResult {
  const [client, loading] = useFetch();
  const api = useMemo(() => createUsersApi(client), [client]);
  const { notifyError } = useNotification();

  const [error, setError] = useState<ApiError | Error | null>(null);

  const assignUserProfile = useCallback(
    async (id: string, profileId: string): Promise<User | null> => {
      try {
        setError(null);
        return await api.assignProfile(id, { profile_id: profileId });
      } catch (err) {
        const e = err as ApiError | Error;
        setError(e);
        // Mutation: 5xx → snackbar; 4xx exposed locally (the page renders it).
        if (ApiError.isApiError(e) && e.isServerError) notifyError(e);
        else if (!ApiError.isApiError(e)) notifyError(e);
        return null;
      }
    },
    [api, notifyError],
  );

  return { assignUserProfile, loading, error };
}
